import { useEffect, useState } from 'react';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton';
import { usePageTitle } from '../../hooks/usePageTitle';
import { loadCardReviewData, CardReviewData } from './cardReviewData';
import CourseReview from './CourseReview';
import EventReview from './EventReview';
import HubReview from './HubReview';
import ProgramReview from './ProgramReview';
import ResourceReview from './ResourceReview';
import {
  CARD_REVIEW_KEY,
  EXAMPLES,
  FAMILIES,
  FAMILY_LABEL,
  CardReviewStore,
  choiceName,
  loadStore,
  saveStore,
  withChoice,
  withNote,
} from './cardReviewStorage';

function Summary({ store }: { store: CardReviewStore }) {
  return (
    <section id="card-review-summary" className="mt-8 p-6 bg-gray-50 border border-gray-200 rounded-lg">
      <h2>Current choices</h2>
      <div className="mt-4 space-y-6">
        {FAMILIES.map((family) => {
          const examples = EXAMPLES.filter((example) => example.family === family);
          return (
            <div key={family} data-summary={family}>
              <p className="font-semibold text-gray-900">{FAMILY_LABEL[family]}</p>
              <ul className="mt-2 space-y-1">
                {examples.map((example) => {
                  const choice = store.choices[example.id];
                  return (
                    <li key={example.id} className="text-sm text-gray-700" data-summary-example={example.id}>
                      {example.label}
                      {choice ? ` — ${choiceName(example.id, choice)}` : ' — not chosen'}
                      {store.notes[example.id]?.trim() && (
                        <span className="block text-gray-600">Note: {store.notes[example.id]}</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default function CardReview() {
  usePageTitle('Card layout review');
  const [store, setStore] = useState<CardReviewStore>(() => loadStore());
  const [data, setData] = useState<CardReviewData | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    saveStore(store);
  }, [store]);

  useEffect(() => {
    let cancelled = false;
    loadCardReviewData()
      .then((loaded) => {
        if (!cancelled) setData(loaded);
      })
      .catch((reason: unknown) => {
        if (!cancelled) {
          setLoadError(reason instanceof Error ? reason.message : 'The review records could not be loaded.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const onChoose = (exampleId: string, optionId: string) => {
    setStore((current) => withChoice(current, exampleId, optionId));
  };

  const onNote = (exampleId: string, note: string) => {
    setStore((current) => withNote(current, exampleId, note));
  };

  const shared = {
    choices: store.choices,
    notes: store.notes,
    onChoose,
    onNote,
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1>Card layout review</h1>
      <p className="mt-3 text-gray-600 max-w-3xl">
        Each place has a note. Type rail is one set. Modern UX is the newer set beside it: the same order on
        every object (what it is, its name, one check, one way in), with a different shape for each type.
        Choices stay in this browser under {CARD_REVIEW_KEY}. Nothing here changes the live pages.
      </p>
      <p className="mt-3 text-gray-600 max-w-3xl">
        Proposed on Type rail and Modern UX, and not on the live site: Enter, Open folder, Open,
        View event, I&apos;ll attend, Start course, and Review course. Type rail says Open document, I&apos;ll attend, and Start course. Modern UX uses a text action for going somewhere, including View event, Watch recording, View course, Continue, and Review course.
        Enrolling stays on the course page. Going and registering stay on the event page. A course meter shows progress. Modern UX turns a finished meter sage. Type rail keeps Completed crimson. On a program,
        a hub row is the way into that hub and Enter is the way into the program. On this board they do not
        navigate. The bookmark on a program is a placeholder for saving a program later. The byline under a
        Modern UX resource title is a placeholder for authors an editor will add later. Save on a hub or
        resource, Add to Calendar, and Watch recording already exist.
      </p>
      <p className="mt-3 text-gray-600 max-w-3xl">
        After you pick a favourite for each place, a fourth version can pull those choices into one set.
      </p>
      <nav className="mt-4 flex flex-wrap gap-x-2" aria-label="Card families">
        {FAMILIES.map((family) => (
          <a
            key={family}
            href={`#review-${family}`}
            className="inline-flex items-center min-h-[44px] px-3 text-sm font-medium text-primary hover:text-primary-dark"
          >
            {FAMILY_LABEL[family]}
          </a>
        ))}
      </nav>
      <Summary store={store} />
      {loadError && <p className="mt-6 text-sm text-error">{loadError}</p>}
      {!data && !loadError && (
        <div className="mt-8">
          <LoadingSkeleton lines={8} />
        </div>
      )}
      {data && (
        <>
          <ProgramReview
            {...shared}
            program={data.program}
            bloodyEasy={data.bloodyEasy}
            notice={data.programNotice}
            error={data.programError}
          />
          <HubReview
            {...shared}
            hub={data.hub}
            programHub={data.programHub}
            notice={data.hubNotice}
            error={data.hubError}
          />
          <ResourceReview
            {...shared}
            post={data.post}
            attachments={data.attachments}
            notice={data.resourceNotice}
            error={data.resourceError}
          />
          <EventReview
            {...shared}
            upcoming={data.upcoming}
            past={data.past}
            notice={data.eventNotice}
            error={data.eventError}
          />
          <CourseReview
            {...shared}
            course={data.course}
            enrollment={data.enrollment}
            notice={data.courseNotice}
            error={data.courseError}
            signedOut={data.courseSignedOut}
          />
        </>
      )}
    </div>
  );
}
